import React from "react";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import { Footer, Header } from "../shared"; // Adjust path as needed
import { motion } from "framer-motion";
import { CustomSnackbar } from "../shared"; // Adjust path as needed
import { rolesAdmin, useSecurityCheck } from "../../utils"; // Adjust path as needed
import { useProviderManager } from "utils/hooks/useProviderManager";
import { QADialog } from "features/info/QADialog";
import { ProviderDetails } from "features/info/ProviderDetails";
import { useUtility } from "utils/context/UtilityContext";

export const ProviderManager = () => {
  const securityCheck = useSecurityCheck(rolesAdmin);
  const hook = useProviderManager();
  const  { marginBottomHeader } = useUtility();

  if (securityCheck) {
    return securityCheck;
  }

  const renderContent = () => {
    if (hook.loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (hook.providers.length === 0) {
      return <Alert severity="info">Aucun fournisseur trouvé.</Alert>;
    }

    return (
      <Grid container spacing={3}>
        {/* Column 1: Provider List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{ maxHeight: { md: "75vh" }, overflowY: "auto" }}
          >
            <List component="nav" dense>
              {hook.providers.map((provider) => (
                <ListItemButton
                  key={provider.name}
                  selected={hook.selectedProvider?.name === provider.name}
                  onClick={() => hook.handleSelectProvider(provider)}
                >
                  <ListItemText primary={provider.name} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Column 2: Provider Details Editor */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, minHeight: "50vh" }}>
            {hook.selectedProvider ? (
              <ProviderDetails
                provider={hook.selectedProvider}
                isSaving={hook.isSaving}
                editDescription={hook.editDescription}
                setEditDescription={hook.setEditDescription}
                editImage={hook.editImage}
                setEditImage={hook.setEditImage}
                editVideoUrl={hook.editVideoUrl}
                setEditVideoUrl={hook.setEditVideoUrl}
                editExternalUrl={hook.editExternalUrl}
                setEditExternalUrl={hook.setEditExternalUrl}
                editAdditionalInfo={hook.editAdditionalInfo}
                setEditAdditionalInfo={hook.setEditAdditionalInfo}
                expandedQA={hook.expandedQA}
                setExpandedQA={hook.setExpandedQA}
                onSaveDetails={hook.handleSaveProviderDetails}
                onAddQA={hook.handleOpenAddQADialog}
                onEditQA={hook.handleOpenEditQADialog}
                onDeleteQA={hook.handleDeleteQA}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                sx={{ minHeight: "50vh" }}
              >
                <Typography variant="h6" color="textSecondary">
                  Sélectionnez un fournisseur pour le modifier
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      className={`flex flex-col min-h-screen ${marginBottomHeader}`}
    >
      <Header />

      <Container maxWidth="xl" sx={{ flexGrow: 1, py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Gestion des Fournisseurs
          </Typography>
          {renderContent()}
        </Stack>
      </Container>

      <Footer />

      <QADialog
        open={hook.openDialog}
        onClose={() => hook.setOpenDialog(false)}
        onSave={hook.handleSaveQA}
        initialData={hook.currentQA}
      />
      <CustomSnackbar
        open={hook.snackbar.open}
        message={hook.snackbar.message}
        isSuccess={hook.snackbar.severity === "success"}
        handleClose={hook.handleSnackbarClose}
      />
    </motion.div>
  );
};

export default ProviderManager;
